import { useState } from 'react';
import { Box, Center, Flex, Group, Loader, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router';
import type { ClientListItem } from 'src/admin/users/types';
import { ChooseClientModal } from 'src/clients/components/ChooseClientModal';
import TextInput from 'src/components/design-system/input/TextInput';
import Textarea from 'src/components/design-system/input/Textarea';
import { Button } from 'src/design-system/Button';
import { IconButton } from 'src/design-system/IconButton';
import { useEvent } from 'src/meetings/hooks/useEvent';
import { useTranscript } from 'src/meetings/hooks/useTranscript';
import { type AssignSpeakerBody, useAssignSpeaker } from 'src/meetings/hooks/useAssignSpeaker';
import { useRegenerateMeetingArtifacts } from 'src/meetings/hooks/useRegenerateMeetingArtifacts';
import { RegenerateArtifactsModal } from 'src/meetings/detail/components/RegenerateArtifactsModal';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { TranscriptionStatus } from 'src/meetings/detail/tabs/transcript/types';
import { TranscriptSegment } from 'src/meetings/detail/tabs/transcript/TranscriptSegment';
import { meetingDetailPath, meetingScopeFromParams, type MeetingScope } from 'src/meetings/scope';
import { formatSpeakerLabel, getUnidentifiedSpeakers } from 'src/meetings/utils/speaker-utils';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

const DESCRIPTION_MAX_LENGTH = 1000;

type SpeakerForm = {
  name: string;
  description: string;
  client: ClientListItem | null;
};

const EMPTY_FORM: SpeakerForm = { name: '', description: '', client: null };

// Returns the label to activate after assigning `assignedLabel`: the next
// unidentified speaker in appearance order, falling back to the previous one
// when the last was assigned, or null when none remain.
function getNextSpeakerLabel(speakers: { label: string }[], assignedLabel: string): string | null {
  const remaining = speakers.filter(speaker => speaker.label !== assignedLabel);
  if (remaining.length === 0) return null;
  const currentIndex = speakers.findIndex(speaker => speaker.label === assignedLabel);
  const nextSpeaker = speakers[currentIndex + 1] ?? speakers[currentIndex - 1];
  return nextSpeaker.label;
}

export default function AssignSpeakerPage() {
  const {
    familyId,
    clientId,
    eventId = '',
    speakerLabel = '',
  } = useParams<{
    familyId?: string;
    clientId?: string;
    eventId: string;
    speakerLabel: string;
  }>();
  const navigate = useNavigate();
  const scope: MeetingScope = meetingScopeFromParams({ familyId, clientId });

  const { event } = useEvent(eventId);
  const { transcript, isLoading } = useTranscript({ eventId });
  const assignSpeaker = useAssignSpeaker(scope, eventId);
  const { flagEnabledRegenerateArtifactsOnParticipantChange } = useFlags();
  const {
    artifacts: regeneratableArtifacts,
    regenerate,
    isRegenerating,
  } = useRegenerateMeetingArtifacts(event);

  const meetingTitle = event?.title;
  const pageTitle = meetingTitle ? `Assign speakers for ${meetingTitle}` : 'Assign speakers';

  const [activeLabel, setActiveLabel] = useState(speakerLabel);
  const [formByLabel, setFormByLabel] = useState<Record<string, SpeakerForm>>({});
  const [chooseClientOpen, setChooseClientOpen] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);

  const meetingPath = meetingDetailPath(scope, eventId);

  if (isLoading && !transcript) {
    return (
      <Center py="xxl">
        <Loader />
      </Center>
    );
  }

  if (transcript?.status !== TranscriptionStatus.READY) {
    return (
      <Box pt={36} px="xxl" pb="xxl">
        <Text c="gray.6">No transcript is available for this meeting.</Text>
      </Box>
    );
  }

  const unidentifiedSpeakers = getUnidentifiedSpeakers(transcript.segments);

  if (unidentifiedSpeakers.length === 0) {
    return (
      <Box pt={36} px="xxl" pb="xxl">
        <Flex align="center" justify="space-between" gap="sm">
          <Text size="displaySm" fw={FONT_WEIGHTS.semibold} c="gray.10">
            {pageTitle}
          </Text>
          <Button variant="text" onClick={() => navigate(meetingPath)}>
            Back to meeting
          </Button>
        </Flex>
        <Text mt="lg" size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6">
          Every speaker in this meeting has been identified.
        </Text>
      </Box>
    );
  }

  const activeSpeaker =
    unidentifiedSpeakers.find(speaker => speaker.label === activeLabel) ?? unidentifiedSpeakers[0];
  const currentLabel = activeSpeaker.label;
  const form = formByLabel[currentLabel] ?? EMPTY_FORM;

  const updateForm = (patch: Partial<SpeakerForm>) =>
    setFormByLabel(previous => ({
      ...previous,
      [currentLabel]: { ...(previous[currentLabel] ?? EMPTY_FORM), ...patch },
    }));

  const speakerSegments =
    transcript.segments?.filter(segment => segment.speaker === currentLabel) ?? [];

  const canAssign = form.client
    ? true
    : form.name.trim().length > 0 && form.description.trim().length > 0;

  const selectedClientName = form.client
    ? formatName(form.client.firstName, form.client.lastName, NameFormat.NATURAL)
    : null;

  async function handleAssign() {
    const correctionLabel = activeSpeaker.correctionLabel;
    const body: AssignSpeakerBody = form.client
      ? {
          speakerLabel: correctionLabel,
          participant: { participantType: 'CLIENT', clientId: form.client.id },
        }
      : {
          speakerLabel: correctionLabel,
          participant: {
            participantType: 'EXTERNAL',
            name: form.name.trim(),
            description: form.description.trim(),
          },
        };
    try {
      await assignSpeaker.mutateAsync(body);
    } catch (error) {
      notifyAndLogError(
        'Failed to identify speaker',
        'Could not assign this speaker. Please try again.',
        error,
      );
      return;
    }

    const nextLabel = getNextSpeakerLabel(unidentifiedSpeakers, currentLabel);
    if (nextLabel !== null) {
      setActiveLabel(nextLabel);
      return;
    }

    // Every speaker is now identified. Changing who spoke can make the transcript
    // summary and downstream documents stale, so offer to regenerate them.
    const shouldPrompt =
      flagEnabledRegenerateArtifactsOnParticipantChange && regeneratableArtifacts.length > 0;
    if (shouldPrompt) {
      setShowRegenerateModal(true);
    } else {
      navigate(meetingPath);
    }
  }

  async function handleConfirmRegenerate() {
    await regenerate();
    setShowRegenerateModal(false);
    navigate(meetingPath);
  }

  return (
    <>
      <Box pt={36} px="xxl" pb="xxl">
        <Flex align="center" justify="space-between" gap="sm">
          <Text size="displaySm" fw={FONT_WEIGHTS.semibold} c="gray.10">
            {pageTitle}
          </Text>
          <Button variant="text" onClick={() => navigate(meetingPath)}>
            Cancel
          </Button>
        </Flex>

        <Flex mt="lg" gap="xxxs">
          {unidentifiedSpeakers.map(speaker => {
            const isActive = speaker.label === currentLabel;
            return (
              <UnstyledButton
                key={speaker.label}
                onClick={() => setActiveLabel(speaker.label)}
                bg={isActive ? 'brand.1' : 'white'}
                py="xxs"
                px="xs"
                style={{ borderRadius: 6 }}
              >
                <Text size="textSm" fw={FONT_WEIGHTS.semibold} c={isActive ? 'brand.8' : 'gray.6'}>
                  {formatSpeakerLabel(speaker.label)}
                </Text>
              </UnstyledButton>
            );
          })}
        </Flex>

        <Box
          mt="lg"
          p={20}
          style={{ border: '1px solid var(--mantine-color-gray-1)', borderRadius: 20 }}
        >
          <Group gap="xxs" align="center">
            <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
              Label this speaker to link their statements or
            </Text>
            <Button
              variant="primary"
              size="sm"
              style={{ borderRadius: 10 }}
              onClick={() => setChooseClientOpen(true)}
            >
              Choose a client
            </Button>
          </Group>

          {form.client ? (
            <Flex
              mt="md"
              align="center"
              justify="space-between"
              gap="sm"
              p="sm"
              bg="gray.1"
              style={{ borderRadius: 12 }}
            >
              <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10">
                {selectedClientName}
              </Text>
              <IconButton
                size={28}
                bg="gray.2"
                c="gray.9"
                aria-label="Clear selected client"
                onClick={() => updateForm({ client: null })}
                style={{ borderRadius: 8 }}
              >
                <IconX size={16} />
              </IconButton>
            </Flex>
          ) : (
            <>
              <Box mt="md">
                <TextInput
                  size="lg"
                  placeholder="Enter speaker name"
                  required
                  value={form.name}
                  onChange={event => updateForm({ name: event.currentTarget.value })}
                  styles={{ input: { border: '1px solid var(--mantine-color-gray-3)' } }}
                />
              </Box>
              <Box mt="xxs" style={{ position: 'relative' }}>
                <Textarea
                  size="lg"
                  placeholder={
                    'Describe who this is - e.g. "David\'s wife" or "solicitor from Thornton & Co"'
                  }
                  required
                  autosize
                  minRows={3}
                  maxLength={DESCRIPTION_MAX_LENGTH}
                  value={form.description}
                  onChange={event => updateForm({ description: event.currentTarget.value })}
                  styles={{
                    input: { border: '1px solid var(--mantine-color-gray-3)', paddingBottom: 28 },
                  }}
                />
                <Flex gap={0} style={{ position: 'absolute', right: 16, bottom: 12 }}>
                  <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10">
                    {form.description.length}
                  </Text>
                  <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.4">
                    /{DESCRIPTION_MAX_LENGTH}
                  </Text>
                </Flex>
              </Box>
            </>
          )}

          <Button
            mt="md"
            variant="primary"
            size="sm"
            w={160}
            h={36}
            style={{ borderRadius: 12 }}
            disabled={!canAssign || assignSpeaker.isPending}
            loading={assignSpeaker.isPending}
            onClick={handleAssign}
          >
            Identify speaker
          </Button>
        </Box>

        <Box
          mt={16}
          p={20}
          style={{ border: '1px solid var(--mantine-color-gray-1)', borderRadius: 20 }}
        >
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="black" pb={16}>
            Statements from this speaker:
          </Text>

          <Stack gap="xxs">
            {speakerSegments.length > 0 ? (
              speakerSegments.map((segment, index) => (
                <TranscriptSegment
                  key={`${segment.startTime}-${index}`}
                  segment={segment}
                  selected={false}
                />
              ))
            ) : (
              <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6">
                This speaker has no statements in the transcript.
              </Text>
            )}
          </Stack>
        </Box>
      </Box>

      <ChooseClientModal
        opened={chooseClientOpen}
        onClose={() => setChooseClientOpen(false)}
        selectedClient={form.client}
        onSelect={client => updateForm({ client })}
      />

      <RegenerateArtifactsModal
        opened={showRegenerateModal}
        onClose={() => {
          setShowRegenerateModal(false);
          navigate(meetingPath);
        }}
        onConfirm={handleConfirmRegenerate}
        artifacts={regeneratableArtifacts}
        isRegenerating={isRegenerating}
      />
    </>
  );
}
